import Alert from '@mui/material/Alert'
import Search from 'UI/Search/Search'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { Flex, Spin } from 'antd'
import cx from 'classnames'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import VerticalTableFilters from 'components/VerticalTableFilters/VerticalTableFilters'
import { PAGE_SIZE } from 'components/verticals/constants'
import CompanySummary from 'containers/TablePageCommon/Company/Summary/CompanySummary'
import { ICompanySummary } from 'containers/TablePageCommon/Company/Summary/types'
import { useDebounce } from 'hooks/useDebounce'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import React, { useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import styles from 'styles/TablePageCommon.scss'
import { CurrentZone } from 'utils/types'
import { selectCurrentZone } from '../../store/global'
import messages from './messages'
import { CompanyOverviewEntityForTable } from './types'
import { useCompanyOverviewMainTableQuery } from './useCompanyOverviewMainTableQuery'
import { useCompanyOverviewOverviewQuery } from './useCompanyOverviewOverviewQuery'
import { companyOverviewColumns, filtersInitialState, keysForEmptyColumnsRequest } from './utils'
import NoDataContainer from 'components/NoDataContainer'
import DownloadPlaybookButton from 'components/DownloadPlaybookButton'
import { VERTICAL_ROUTE_MAP } from 'config/verticalRoutes'

const CompanyOverviewTablePage = () => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { searchValue, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])

  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.companyOverviewIndices,
    currentQueryKey: 'companyOverview-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData } = useCompanyOverviewOverviewQuery({
    verticalIndices: currentZone?.companyOverviewIndices,
    currentQueryKey: 'company-overview-overview-key',
    searchValue,
    filters: activeFiltersDebounced,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useCompanyOverviewMainTableQuery<CompanyOverviewEntityForTable>({
      verticalIndices: currentZone?.companyOverviewIndices,
      currentQueryKey: 'company-overview-entities-key',
      searchValue,
      filters: activeFiltersDebounced,
      sorting,
      pageSize: PAGE_SIZE,
    })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: currentZone?.companyOverviewIndices,
    currentQueryKey: 'company-overview-filter-options-key',
    filtersInitialState,
  })

  const overviewSummaryData = useMemo(() => {
    if (!overviewData) return null
    const overviewDataCopy = { ...overviewData }
    delete overviewDataCopy.total
    return overviewDataCopy
  }, [overviewData])

  const hasNoData = useMemo(() => {
    return (
      !isLoading &&
      !isError &&
      (!verticalEntities || verticalEntities.length === 0) &&
      !searchValue &&
      activeFiltersDebounced.length === 0
    )
  }, [isLoading, isError, verticalEntities, searchValue, activeFiltersDebounced])

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <nav id='navigationHeaderWrapper' className={styles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
          {!hasNoData && <DownloadPlaybookButton verticalId={VERTICAL_ROUTE_MAP.companyOverview.id} />}
        </header>
      </div>
      {hasNoData ? (
        <NoDataContainer verticalName='Company Overview' zoneName={currentZone?.name} />
      ) : (
        <>
          <div ref={statisticsActivityRef} className={styles.summary}>
            <CompanySummary summaryData={overviewSummaryData as ICompanySummary} />
          </div>
          {isLoading ? (
            <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
              <Spin size='large' />
            </Flex>
          ) : isError ? (
            <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
              Something went wrong
            </Alert>
          ) : (
            <>
              {emptyColumnsIsFetched && (
                <VerticalTable
                  pageSize={PAGE_SIZE}
                  enableColumnPicker
                  tableId='company_overview'
                  dataSource={verticalEntities as any[]}
                  columns={companyOverviewColumns}
                  containerRef={containerRef}
                  additionalRefs={[statisticsActivityRef, navigationRef]}
                  fetchNextPage={fetchNextPage}
                  total={overviewData?.total || 0}
                  loading={isFetching}
                  disabledColumns={emptyColumnsData}
                  onSelectionChange={setSelectedIds}
                  removeFromPickerColumns={[COLUMNS_IDS.verifiedName]}
                  rowClassName={styles.tableRow}
                  onRow={record => {
                    return {
                      onClick: () => {
                        window.open(`${window.location.origin}/vertical/company-overview/${record.id}`, '_blank')
                      },
                    }
                  }}
                  onChange={(pagination, filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                      if (sorter.order) {
                        setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                      } else {
                        setSorting([])
                      }
                    }
                  }}
                >
                  <div className={styles.counter}>
                    {`Company Overview: `}
                    <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
                  </div>
                  <VerticalTableFilters
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                    filtersInitialState={filtersInitialState}
                    filterOptions={filterOptions}
                    filterAdditionalData={[]}
                  />
                  <Search
                    placeholder='input search text'
                    enterButton
                    allowClear
                    style={{ width: 264 }}
                    loading={isFetching}
                    onSearch={newSearchValue => setSearchValue(newSearchValue)}
                  />
                  <ExportCSVTableVertical
                    zoneIndex={currentZone?.companyOverviewIndices}
                    csvFileName='CompanyOverview'
                    searchValue={searchValue}
                    selectedIds={selectedIds}
                    sorting={sorting}
                    total={overviewData?.total}
                  />
                </VerticalTable>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default CompanyOverviewTablePage
